import ProductName from '@web/components/ProductName'
import SavingsCalculator from '@web/sections/Products/SavingsCalculator'
import { ChevronDown } from 'lucide-react'

type Tier = {
  size: string
  population: string
  licence: string
}

const tiers: Tier[] = [
  { size: 'Size 1', population: 'Under 10,000', licence: '$13,100' },
  { size: 'Size 2', population: '10,000 to 35,000', licence: '$15,900' },
  { size: 'Size 3', population: '35,000 to 100,000', licence: '$20,100' },
  { size: 'Size 4', population: '100,000 to 200,000', licence: '$24,200' },
  { size: 'Size 5', population: 'Over 200,000', licence: '$28,400' },
]

const Pricing = () => {
  return (
    <section className="bg-neutral-muted py-24 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl px-6 lg:px-8">
        <div className="space-y-3 max-w-3xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-blue">
            What it costs &middot; What it saves
          </span>
          <h2 className="text-4xl font-light text-brand-blue m-0">
            Pricing is published. So is the return.
          </h2>
          <p className="text-base text-brand-navy/90 leading-relaxed m-0">
            <ProductName /> is licensed per council per year. The annual licence typically pays back
            within 2 to 8 months on staff time savings alone, before counting reduced consultant
            fees or avoided hires.
          </p>
        </div>

        <details open className="group rounded-lg bg-white shadow-sm overflow-hidden">
          <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-4 px-6 py-5 bg-brand-navy text-white hover:bg-brand-navy/90 transition-colors">
            <span className="text-sm font-semibold uppercase tracking-widest">
              See pricing and the savings calculator
            </span>
            <ChevronDown
              className="h-5 w-5 shrink-0 transition-transform duration-200 group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>

          <div className="p-6 lg:p-8">
            <div className="grid gap-10 lg:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-brand-blue m-0">
                  What it costs
                </h3>

                <div className="hidden sm:block rounded-lg bg-neutral-muted shadow-sm overflow-hidden">
                  <table className="w-full text-left tabular-nums">
                    <thead>
                      <tr className="bg-brand-navy text-white">
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                          Council size
                        </th>
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider">
                          Population
                        </th>
                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-right">
                          Annual licence
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {tiers.map((t, idx) => (
                        <tr
                          key={t.size}
                          className={idx % 2 === 0 ? 'bg-white' : 'bg-neutral-muted/50'}
                        >
                          <td className="px-5 py-3 text-sm font-semibold text-brand-navy">
                            {t.size}
                          </td>
                          <td className="px-5 py-3 text-sm text-brand-navy/80">{t.population}</td>
                          <td className="px-5 py-3 text-sm font-semibold text-brand-blue text-right">
                            {t.licence}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="sm:hidden space-y-3">
                  {tiers.map((t) => (
                    <div
                      key={t.size}
                      className="rounded-lg bg-neutral-muted p-5 space-y-2 tabular-nums"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="text-base font-semibold text-brand-navy m-0">{t.size}</h3>
                        <span className="text-base font-semibold text-brand-blue">{t.licence}</span>
                      </div>
                      <p className="text-sm text-brand-navy/80 m-0">{t.population}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 text-sm text-brand-navy/80">
                  <p className="m-0">
                    <strong>Multi-year commitments.</strong> Three-year terms attract a 7.5%
                    discount on annual licence. Five-year terms attract 15%.
                  </p>
                  <p className="m-0 font-semibold text-brand-navy">
                    All prices in NZD, GST exclusive.
                  </p>
                  <p className="m-0">
                    <strong>Already a Resilience Explorer® client?</strong> <ProductName /> is
                    included as a feature of the Professional and Enterprise tiers. Talk to your
                    Customer Success contact about activation. No new procurement needed.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-brand-blue m-0">
                  What it saves
                </h3>
                <SavingsCalculator />
                <p className="text-sm text-brand-navy/80 leading-relaxed m-0">
                  Calculation covers LIM staff time only. <ProductName /> typically also reduces
                  external hazards consulting fees by 50% to 70%, avoids 0.5 FTE in operational
                  growth as LIM volumes increase, and cuts rework rates. Most councils see total
                  savings of two to three times the staff-time figure above. Talk to us at a
                  walkthrough for a full picture against your numbers.
                </p>
              </div>
            </div>
          </div>
        </details>
      </div>
    </section>
  )
}

export default Pricing
